import React, { useState } from 'react';
import {
  Container,
  Grid,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Rating
} from '@mui/material';
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount';
import AssessmentIcon from '@mui/icons-material/Assessment';
import BuildIcon from '@mui/icons-material/Build';

const SchoolSupervision = () => {
  const [selectedTeacher, setSelectedTeacher] = useState('');
  const [selectedClass, setSelectedClass] = useState('');

  // Sample data - replace with actual data from your backend
  const supervisionData = {
    teacherPerformance: [
      { id: 1, name: 'John Doe', subject: 'Mathematics', rating: 4.5, lastObservation: '2024-01-15' },
      { id: 2, name: 'Jane Smith', subject: 'English', rating: 4.8, lastObservation: '2024-01-18' },
      { id: 3, name: 'Robert Johnson', subject: 'Science', rating: 4.2, lastObservation: '2024-01-14' },
    ],
    classObservations: [
      { id: 1, class: 'Class 3', date: '2024-01-19', observer: 'Head Teacher', status: 'Completed' },
      { id: 2, class: 'Class 4', date: '2024-01-20', observer: 'Deputy Head', status: 'Scheduled' },
      { id: 3, class: 'Class 2', date: '2024-01-21', observer: 'Head Teacher', status: 'Pending' },
    ]
  };

  const resourceCards = [
    {
      title: 'Teaching Materials',
      status: 'Adequate',
      lastUpdate: '2024-01-15',
      icon: <AssessmentIcon sx={{ fontSize: 40, color: 'success.main' }} />
    },
    {
      title: 'Infrastructure',
      status: 'Good',
      lastUpdate: '2024-01-10',
      icon: <BuildIcon sx={{ fontSize: 40, color: 'primary.main' }} />
    },
    {
      title: 'Learning Resources',
      status: 'Need Review',
      lastUpdate: '2024-01-18',
      icon: <SupervisorAccountIcon sx={{ fontSize: 40, color: 'warning.main' }} />
    }
  ];

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" gutterBottom>
        School Supervision Dashboard
      </Typography>

      <Grid container spacing={3}>
        {/* Teacher Performance Section */}
        <Grid item xs={12}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Teacher Performance Monitoring
            </Typography>
            <Table size="medium">
              <TableHead>
                <TableRow>
                  <TableCell>Teacher Name</TableCell>
                  <TableCell>Subject</TableCell>
                  <TableCell>Performance Rating</TableCell>
                  <TableCell>Last Observation</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {supervisionData.teacherPerformance.map((teacher) => (
                  <TableRow key={teacher.id}>
                    <TableCell>{teacher.name}</TableCell>
                    <TableCell>{teacher.subject}</TableCell>
                    <TableCell>
                      <Rating value={teacher.rating} precision={0.1} readOnly />
                    </TableCell>
                    <TableCell>{teacher.lastObservation}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>
        </Grid>

        {/* Classroom Observations Section */}
        <Grid item xs={12}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Classroom Observations
            </Typography>
            <Table size="medium">
              <TableHead>
                <TableRow>
                  <TableCell>Class</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Observer</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {supervisionData.classObservations.map((observation) => (
                  <TableRow key={observation.id}>
                    <TableCell>{observation.class}</TableCell>
                    <TableCell>{observation.date}</TableCell>
                    <TableCell>{observation.observer}</TableCell>
                    <TableCell>{observation.status}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>
        </Grid>

        {/* Resource Cards */}
        {resourceCards.map((card, index) => (
          <Grid item xs={12} md={4} key={index}>
            <Paper sx={{ p: 2, display: 'flex', alignItems: 'center' }}>
              <Box sx={{ mr: 2 }}>{card.icon}</Box>
              <Box>
                <Typography variant="h6" gutterBottom>
                  {card.title}
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  Status: {card.status}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Last Updated: {card.lastUpdate}
                </Typography>
              </Box>
            </Paper>
          </Grid>
        ))}

        {/* Add Observation Form */}
        <Grid item xs={12}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Schedule New Observation
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Select Teacher</InputLabel>
                  <Select
                    value={selectedTeacher}
                    label="Select Teacher"
                    onChange={(e) => setSelectedTeacher(e.target.value)}
                  >
                    {supervisionData.teacherPerformance.map((teacher) => (
                      <MenuItem key={teacher.id} value={teacher.id}>
                        {teacher.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Select Class</InputLabel>
                  <Select
                    value={selectedClass}
                    label="Select Class"
                    onChange={(e) => setSelectedClass(e.target.value)}
                  >
                    <MenuItem value="class1">Class 1</MenuItem>
                    <MenuItem value="class2">Class 2</MenuItem>
                    <MenuItem value="class3">Class 3</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <Button variant="contained" color="primary">
                  Schedule Observation
                </Button>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
};

export default SchoolSupervision;
